/*
  Holster Finder
  Make > Model > Undercarriage, per the glossary in CONTEXT.md.

  Two data sources, deliberately:

  1. A generated snippet inlined by the section, read synchronously at init.
     It paints the dropdowns immediately, so every field is usable on the first
     tap. An earlier version fetched its data and left the Make field disabled
     until the round trip finished, which made the first tap wake the field and
     the second open it: the same defect this project spent a day removing from
     the Boost search box. Never make the first paint wait on a network call.

  2. The live search API, which is the source of TRUTH. The snapshot in the
     snippet includes draft and archived products, so on its own it offers
     combinations that return nothing. The API knows only the active catalogue
     and carries real result counts, and it needs no regeneration when products
     are released. Live values replace the snapshot as soon as they arrive.

  If the API is unreachable the picker keeps working on the snapshot alone, so
  an outage degrades to slightly stale rather than to an empty form.

  Everything engine-specific lives in ENGINE, so a future migration is one
  object rather than a rewrite.
*/
(function () {
  "use strict";

  var SELECTOR = "[data-rg-holster-finder]";
  var NO_LIGHT = "No Light/Laser";

  function labelOf(entry) {
    return (typeof entry === "string") ? entry : entry.label;
  }

  /*
    Every list is alphabetical. The engine returns facets ordered by result
    count, which is useless for finding your own gun: a shopper scanning for
    "Ruger" expects it between "Kimber" and "SAR", not wherever its stock
    level happens to put it. "No Light/Laser" is the one exception and always
    leads, because it is the answer for most people.
  */
  function alphabetical(entries) {
    return entries.slice().sort(function (a, b) {
      var left = labelOf(a);
      var right = labelOf(b);
      if (left === NO_LIGHT) return -1;
      if (right === NO_LIGHT) return 1;
      return left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
    });
  }

  var ENGINE = {
    resultsPath: "/search",

    /*
      The facet id and the storefront query parameter are the same string. The
      API wants it suffixed with [], the storefront URL does not. Three
      different namespace casings appear here, which is Boost's own
      inconsistency: my_fields and gun_attributes are lower case,
      GUN_ATTRIBUTES is upper. A wrong name does not error, it silently returns
      an UNFILTERED result, which looks like a working page with the wrong
      products on it. Hence constants, never hand-built strings.
    */
    params: {
      make: "pf_m_::my_fields::gun_make",
      model: "pf_m_::gun_attributes::supported_gun_models",
      undercarriage: "pf_m_::GUN_ATTRIBUTES::UNDERCARRIAGE"
    },

    apiBase: "https://services.mybcapps.com/bc-sf-filter/filter",

    /* Best sellers first. Relevance ranked a niche Sidecar above the core IWB
       holsters for the same gun, which is the wrong first impression. */
    sort: "best-selling",

    buildUrl: function (selection, options) {
      var qs = new URLSearchParams();
      qs.append(ENGINE.params.model, selection.model);

      /*
        Universal products split into two kinds that want opposite treatment,
        and one condition separates them.

        LIGHT-FIT products fit one specific light on ANY gun, so they belong in
        a result whenever that light is the one chosen. Eight of these exist:
        the TLR1, TLR7 and X300 holsters.

        PLAIN universal holsters carry "No Light/Laser". They are not usefully
        gun-agnostic, and putting them in front of someone who named their
        exact gun buries the holster actually built for it. Six of these exist.

        So the universal models are ORed in only when a real light is chosen.
        The undercarriage filter then keeps just the light-fits that match, and
        the plain ones never appear because they carry "No Light/Laser".

        The plain six should instead gain real fitment data for the popular
        guns they suit but that have no dedicated holster.
      */
      var wantsLight = selection.undercarriage && selection.undercarriage !== NO_LIGHT;
      // And only for a light some universal holster actually fits. Without
      // this an Integrated CTC Laser query ORed in Universal, adding a filter
      // chip that matched nothing and read as a bug.
      var covered = (options.universalUndercarriages || [])
        .indexOf(selection.undercarriage) !== -1;
      if (options.includeLightFit && wantsLight && covered) {
        options.universalModels.forEach(function (value) {
          if (value !== selection.model) qs.append(ENGINE.params.model, value);
        });
      }
      if (selection.undercarriage) {
        qs.append(ENGINE.params.undercarriage, selection.undercarriage);
      }
      qs.append("sort", ENGINE.sort);
      return ENGINE.resultsPath + "?" + qs.toString();
    },

    readUrl: function (search, universalModels) {
      var qs = new URLSearchParams(search || "");
      var models = qs.getAll(ENGINE.params.model).filter(function (value) {
        return universalModels.indexOf(value) === -1;
      });
      if (!models.length) return null;
      return {
        model: models[0],
        undercarriage: qs.get(ENGINE.params.undercarriage) || null
      };
    },

    /*
      Drop only the fitment filter, keeping any other refinement the shopper
      has added since. Clearing the gun should not throw away their carry type
      or price filter too.
    */
    clearUrl: function (search) {
      var qs = new URLSearchParams(search || "");
      qs.delete(ENGINE.params.model);
      qs.delete(ENGINE.params.undercarriage);
      qs.delete("sort");
      var rest = qs.toString();
      return ENGINE.resultsPath + (rest ? "?" + rest : "");
    },

    /*
      Ask the engine what is actually available, optionally narrowed by a
      partial selection. Resolves to { total, makes, models, lights }, each a
      list of { label, count } over the ACTIVE catalogue only.
    */
    fetchFacets: function (shop, selection) {
      var qs = new URLSearchParams({
        shop: shop,
        page: "1",
        limit: "1",
        sort: "relevance",
        locale: "en",
        collection_scope: "0",
        build_filter_tree: "true"
      });
      // The API form of each parameter takes a [] suffix.
      if (selection && selection.make) qs.append(ENGINE.params.make + "[]", selection.make);
      if (selection && selection.model) qs.append(ENGINE.params.model + "[]", selection.model);
      if (selection && selection.undercarriage) {
        qs.append(ENGINE.params.undercarriage + "[]", selection.undercarriage);
      }

      return fetch(ENGINE.apiBase + "?" + qs.toString(), { credentials: "omit" })
        .then(function (response) {
          if (!response.ok) throw new Error("HTTP " + response.status);
          return response.json();
        })
        .then(function (data) {
          var byId = {};
          ((data.filter && data.filter.options) || []).forEach(function (facet) {
            byId[facet.filterOptionId] = (facet.values || []).map(function (value) {
              return {
                label: value.label || value.key,
                count: typeof value.doc_count === "number" ? value.doc_count : value.count
              };
            });
          });
          return {
            total: data.total_product,
            makes: byId[ENGINE.params.make] || [],
            models: byId[ENGINE.params.model] || [],
            lights: byId[ENGINE.params.undercarriage] || []
          };
        });
    }
  };

  /* ------------------------------------------------------------------- */

  /*
    Publish the height of whatever chrome is pinned to the top of the viewport,
    so the docked bar can sit directly beneath it.

    The theme offers nothing to inherit: --sticky-header-height is set on
    .sticky_nav for its own dropdown, not on root, and there is no other
    position: sticky in the theme. So this measures the real geometry.

    Measuring getBoundingClientRect().bottom rather than the height is
    deliberate. It accounts for the promo banner above the header, for the
    header shrinking on scroll, and for the banner being dismissed, without
    needing to know that any of those exist. A header that is not pinned
    contributes nothing, which is the desktop case.
  */
  function publishDockOffset() {
    var pinned = 0;
    var candidates = document.querySelectorAll("#header, .sticky_nav, .shopify-section-header");
    for (var i = 0; i < candidates.length; i++) {
      var node = candidates[i];
      var position = getComputedStyle(node).position;
      if (position !== "fixed" && position !== "sticky") continue;
      var bottom = node.getBoundingClientRect().bottom;
      if (bottom > pinned) pinned = bottom;
    }
    document.documentElement.style.setProperty(
      "--rg-hf-dock-top", Math.max(0, Math.round(pinned)) + "px"
    );
  }

  function watchDockOffset() {
    publishDockOffset();
    var queued = false;
    var recheck = function () {
      if (queued) return;
      queued = true;
      // rAF rather than a timer, so it settles with the paint the header moved in.
      window.requestAnimationFrame(function () {
        queued = false;
        publishDockOffset();
      });
    };
    window.addEventListener("scroll", recheck, { passive: true });
    window.addEventListener("resize", recheck, { passive: true });
    window.addEventListener("orientationchange", recheck);
  }

  /*
    Boost rewrites the URL when a shopper clears or edits a filter from its own
    panel, without reloading. If our gun filter goes with it, the bar would
    keep claiming a gun that is no longer applied, which is worse than showing
    nothing: it is a confident wrong answer.

    pushState and replaceState do not fire an event, so they are wrapped once
    to emit one. popstate covers the back button.
  */
  function watchUrlChanges(onChange) {
    if (watchUrlChanges.installed) {
      watchUrlChanges.handlers.push(onChange);
      return;
    }
    watchUrlChanges.installed = true;
    watchUrlChanges.handlers = [onChange];

    var fire = function () {
      watchUrlChanges.handlers.forEach(function (fn) { fn(); });
    };
    ["pushState", "replaceState"].forEach(function (name) {
      var original = history[name];
      if (typeof original !== "function") return;
      history[name] = function () {
        var result = original.apply(this, arguments);
        // after the URL has actually changed
        window.setTimeout(fire, 0);
        return result;
      };
    });
    window.addEventListener("popstate", fire);
  }

  function readSnapshot(root) {
    var node = root.querySelector("[data-rg-hf-data]") ||
               document.querySelector("[data-rg-hf-data]");
    if (!node) return null;
    try {
      var data = JSON.parse(node.textContent);
      return (data && data.makes) ? data : null;
    } catch (error) {
      return null;
    }
  }

  /*
    Model values carry their make, e.g. "Smith & Wesson M&P SHIELD M2.0 3.1in
    9MM/40SW". The make is already chosen in the field above, so repeating it
    wastes the line and wraps on a phone. Strip it for display only.
  */
  /*
    Drop the light's maker for display. A shopper who has told us they run a
    TLR-7 does not need to be told it is a Streamlight, and the brand is what
    pushes the longest combinations past two lines on a phone: measured at
    375px, "Smith & Wesson M&P SHIELD M2.0 3.1in 9MM/40SW with Streamlight
    TLR-7/7A/7X" needs 493px against 235px of room, which is three lines.
    Without the maker it is 423px, which fits two.

    Display only. The value handed to the engine is never touched, because it
    has to match the product data exactly.
  */
  var LIGHT_MAKERS = /^(Streamlight|Olight|Surefire|Viridian|Crimson Trace)\s+/;

  function shortLight(value) {
    return value.replace(LIGHT_MAKERS, "");
  }

  function withoutMake(make) {

    return function (model) {
      return (make && model.indexOf(make + " ") === 0) ? model.slice(make.length + 1) : model;
    };
  }

  /*
    Analytics. Pushed to the GTM dataLayer, the route every other tag on this
    store already takes.

    IT MUST NEVER BREAK THE PICKER. The whole body is wrapped in try/catch and
    no caller reads a return value, so a missing dataLayer, a blocked tag
    manager or an ad blocker costs a measurement and nothing else. A finder
    that threw because a beacon failed would be a far worse bug than the one
    this exists to answer.

    THE dl_ PREFIX IS DELIBERATELY AVOIDED. Elevar owns the data layer here and
    uses dl_ for its own events, which are load-bearing for attribution. These
    are namespaced holster_finder_* so they cannot collide. Verify in Elevar's
    debugger and GTM Preview before trusting the numbers: a catch-all trigger
    in the container would pick these up too.

    Values are stringified and capped at 100 characters, the GA4 event
    parameter limit. GA4 DROPS an over-long value rather than truncating it,
    which would read as missing data rather than as a limit.
  */
  /*
    Where the picker was used, so homepage and search usage can be told apart
    without a second GA4 dimension. Derived from the path rather than passed in
    from Liquid: the section is placed through the theme editor and could land
    on any template, and a path is true wherever it ends up.
  */
  function templateName() {
    try {
      var path = window.location.pathname;
      if (path === "/") return "index";
      if (path.indexOf("/search") === 0) return "search";
      if (path.indexOf("/collections") === 0) return "collection";
      if (path.indexOf("/products") === 0) return "product";
      return "other";
    } catch (e) {
      return null;
    }
  }

  /*
    hydrateFromUrl runs MORE THAN ONCE per page load: init calls it, and so
    does syncToUrl when the URL watcher sees Boost push history state, which
    Boost does on load. Both are correct for rendering, because re-applying
    the same selection is a no-op. But firing the event twice would DOUBLE the
    one metric this event exists to provide: the reliable denominator for
    outcome analysis. Measured on staging before this guard: two identical
    holster_finder_results events on a single page load.

    Deduped on the SELECTION, not "once per page". A shopper who changes gun
    is genuinely looking at a new set of results and that deserves its own
    event; only the repeat of an unchanged selection is suppressed.
  */
  var lastResultsKey = null;

  function track(name, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: name };
      if (params) {
        for (var key in params) {
          if (!Object.prototype.hasOwnProperty.call(params, key)) continue;
          if (params[key] === null || params[key] === undefined || params[key] === "") continue;
          payload[key] = String(params[key]).slice(0, 100);
        }
      }
      window.dataLayer.push(payload);
    } catch (e) {
      /* measurement is never worth an exception on the buying path */
    }
  }

  function HolsterFinder(root) {
    this.root = root;
    /*
      root is the FORM. The heading and subheading are its siblings, not its
      descendants, so anything that has to hide them must be toggled on the
      section wrapper instead. Setting it on the form silently did nothing:
      the CSS is scoped to .rg-hf and the form never carries that class.
    */
    this.section = root.closest("[data-rg-hf-section]") || root;
    /*
      A sticky element can only travel inside its own parent's box, and the
      section wrapper Shopify emits is exactly as tall as the bar, so sticky
      applied to .rg-hf had nowhere to go. Measured on staging: bar 45px,
      parent 45px, room to travel 0. The wrapper's own parent is the page
      column at 2890px, so the wrapper is what has to stick.
    */
    this.dock = this.section.closest(".shopify-section") || this.section;
    this.makeField = root.querySelector("[data-rg-hf-make]");
    this.modelField = root.querySelector("[data-rg-hf-model]");
    this.underField = root.querySelector("[data-rg-hf-undercarriage]");
    this.underWrap = root.querySelector("[data-rg-hf-undercarriage-field]");
    this.submit = root.querySelector("[data-rg-hf-submit]");
    this.status = root.querySelector("[data-rg-hf-status]");
    this.summary = root.querySelector("[data-rg-hf-summary]");
    this.summaryText = root.querySelector("[data-rg-hf-summary-text]");
    this.clearButton = root.querySelector("[data-rg-hf-clear]");
    this.changeButton = root.querySelector("[data-rg-hf-change]");
    // On mobile the field label is visually hidden, so this placeholder is the
    // only context the shopper has for what the field is asking.
    this.underPlaceholder = (this.underField &&
      this.underField.getAttribute("data-rg-hf-undercarriage-placeholder")) || "Select Light Or Laser";

    this.shop = root.getAttribute("data-rg-hf-shop") || "";
    this.includeLightFit = root.getAttribute("data-rg-hf-lightfit") !== "false";

    /*
      Absent on the search page and the homepage, which is how the default
      layout keeps rendering exactly as it shipped. See the note in
      sections/rg-holster-finder.liquid.
    */
    this.layout = this.section.getAttribute("data-rg-hf-layout") || "full";
    this.makeHint = this.section.getAttribute("data-rg-hf-make-hint") || "";
    this.titleHint = this.section.getAttribute("data-rg-hf-title-hint") || "";

    this.snapshot = readSnapshot(root);
    if (!this.snapshot) {
      this.fail();
      return;
    }

    // One token per field. A slow response for a make the shopper has already
    // moved on from must not overwrite the list they are looking at now.
    this.token = { make: 0, model: 0, light: 0 };

    this.bind();
    this.bindPill();
    this.populateMakes();
    /*
      Before hydrateFromUrl, never after: a make and model in the URL is an
      explicit choice and must win over the page's own default. Prefill only
      sets the make, so hydrate then overwrites it with the real selection.
    */
    this.prefillMake();
    this.hydrateFromUrl();
    this.refreshMakesLive();

    var self = this;
    watchUrlChanges(function () { self.syncToUrl(); });
  }

  HolsterFinder.prototype.bind = function () {
    var self = this;
    this.makeField.addEventListener("change", function () { self.onMakeChange(); });
    this.modelField.addEventListener("change", function () { self.onModelChange(); });
    this.underField.addEventListener("change", function () { self.setStatus(""); });
    this.root.addEventListener("submit", function (event) {
      event.preventDefault();
      self.go();
    });
    if (this.clearButton) {
      this.clearButton.addEventListener("click", function (event) {
        event.preventDefault();
        window.location.href = ENGINE.clearUrl(window.location.search);
      });
    }
    if (this.changeButton) {
      this.changeButton.addEventListener("click", function (event) {
        event.preventDefault();
        self.expand();
      });
    }
  };

  HolsterFinder.prototype.fail = function () {
    this.root.setAttribute("data-rg-hf-failed", "true");
    this.setStatus("The gun list is unavailable. Search instead.");
  };

  HolsterFinder.prototype.setStatus = function (message) {
    if (this.status) this.status.textContent = message || "";
  };

  /*
    values: array of strings, or of { label, count } when counts are known.
    The option VALUE is always the untouched string, because it is handed
    straight to the engine as a filter and must match the product data exactly.
    Only the visible text changes.
  */
  HolsterFinder.prototype.fill = function (field, values, placeholder, label) {
    var previous = field.value;
    values = alphabetical(values);
    field.innerHTML = "";

    var first = document.createElement("option");
    first.value = "";
    first.textContent = placeholder;
    field.appendChild(first);

    values.forEach(function (entry) {
      var value = labelOf(entry);
      var option = document.createElement("option");
      option.value = value;
      option.textContent = label ? label(value) : value;
      field.appendChild(option);
    });

    // Keep the shopper's choice when a live list replaces the snapshot one.
    if (previous) field.value = previous;
    field.disabled = values.length === 0;
  };

  /*
    Phone only, driven by CSS: hide the model field until a make is chosen.
    An empty "Select A Make First" control is a prompt to do nothing, and it
    costs a field's height in the resting state.
  */
  HolsterFinder.prototype.reflectMakeState = function () {
    if (this.makeField.value) {
      this.section.removeAttribute("data-rg-hf-awaiting-make");
    } else {
      this.section.setAttribute("data-rg-hf-awaiting-make", "true");
    }
  };

  HolsterFinder.prototype.populateMakes = function () {
    this.fill(this.makeField, Object.keys(this.snapshot.makes), "Select Make");
    this.makeField.disabled = false;
    this.reflectMakeState();
  };

  /*
    Resolve the page's make against the snapshot, which is the only list that
    knows what fitment data actually exists.

    The metafield is tried first and the collection title second. The title is
    a fallback, not the mechanism: B27 renames makes and models, and a rename
    changes titles while leaving the metafield intact.

    Returns "" when nothing matches, and that case is the point of the
    function rather than an edge of it. Colt has a collection, a nav entry and
    a populated metafield, and no models in the fitment data. Returning ""
    leaves the shopper the full picker instead of an empty model list sitting
    under a "300+ models covered" claim.
  */
  HolsterFinder.prototype.resolveHintedMake = function () {
    var makes = Object.keys(this.snapshot.makes);
    if (this.makeHint && makes.indexOf(this.makeHint) !== -1) return this.makeHint;

    var title = (this.titleHint || "").toLowerCase();
    if (!title) return "";
    var hit = "";
    for (var i = 0; i < makes.length; i++) {
      var name = makes[i].toLowerCase();
      // Word-boundary-ish: "CZ" must not match "CZECH", and a title of
      // "SIG SAUER HOLSTERS" must still find "Sig Sauer".
      if (title === name || title.indexOf(name + " ") === 0 || title.indexOf(" " + name + " ") !== -1) {
        // Longest wins, so "Smith & Wesson" beats a hypothetical "Smith".
        if (name.length > hit.length) hit = makes[i];
      }
    }
    return hit;
  };

  HolsterFinder.prototype.prefillMake = function () {
    if (this.layout === "full") return;
    var make = this.resolveHintedMake();
    if (!make) return;
    this.makeField.value = make;
    this.onMakeChange();
    this.setPillLabel(make);
    /*
      Only once a make actually resolved. Setting it from the Liquid hint would
      hide the make select on a Colt page, leaving a shopper with one disabled
      model field and no way back.
    */
    this.section.classList.add("rg-hf--make-locked");
  };

  /*
    In a compact layout the make is either already known or has just been
    chosen, so naming it in the placeholder is the whole of the "one field"
    idea: the single visible control says which gun it is asking about.
  */
  HolsterFinder.prototype.modelPlaceholderFor = function (make) {
    if (this.layout === "full" || !make) return "Select Model";
    return "Select Your " + make + " Model";
  };

  HolsterFinder.prototype.setPillLabel = function (make) {
    var el = this.pill && this.pill.querySelector("[data-rg-hf-pill-text]");
    if (el && make) el.textContent = "Find Your " + make;
  };

  /*
    The pill ships expanded and is collapsed here, so a shopper without JS
    gets a usable form rather than a button that does nothing.
  */
  HolsterFinder.prototype.bindPill = function () {
    this.pill = this.section.querySelector("[data-rg-hf-pill]");
    if (!this.pill) return;
    var self = this;
    this.setPillOpen(false);
    this.pill.addEventListener("click", function () {
      self.setPillOpen(self.pill.getAttribute("aria-expanded") !== "true");
    });
  };

  HolsterFinder.prototype.setPillOpen = function (open) {
    if (!this.pill) return;
    this.pill.setAttribute("aria-expanded", open ? "true" : "false");
    this.section.classList.toggle("rg-hf--open", open);
    if (!open) return;
    /*
      Focus the first field the shopper still has to answer. Opening is a
      user gesture, so the height change it causes is excluded from CLS.
    */
    var first = this.root.querySelector(".rg-hf__field:not([hidden]) select:not([disabled])");
    if (first) first.focus();
  };

  /* Replace the snapshot lists with what the engine actually has. */
  HolsterFinder.prototype.refreshMakesLive = function () {
    if (!this.shop) return;
    var self = this;
    var mine = ++this.token.make;
    ENGINE.fetchFacets(this.shop, {}).then(function (live) {
      if (mine !== self.token.make || !live.makes.length) return;
      self.fill(self.makeField, live.makes, "Select Make");
      self.makeField.disabled = false;
    }).catch(function () { /* snapshot already painted; nothing to do */ });
  };

  HolsterFinder.prototype.onMakeChange = function () {
    var make = this.makeField.value;
    var models = make ? Object.keys(this.snapshot.makes[make] || {}) : [];

    this.fill(
      this.modelField,
      models,
      models.length ? this.modelPlaceholderFor(make) : "Select A Make First",
      withoutMake(make)
    );
    this.fill(this.underField, [], "Select Model First");
    this.showUndercarriage(false);
    this.reflectMakeState();
    this.setStatus("");

    if (!make || !this.shop) return;
    var self = this;
    var mine = ++this.token.model;
    ENGINE.fetchFacets(this.shop, { make: make }).then(function (live) {
      if (mine !== self.token.model || self.makeField.value !== make) return;
      if (!live.models.length) return;
      self.fill(self.modelField, live.models, self.modelPlaceholderFor(make), withoutMake(make));
    }).catch(function () {});
  };

  HolsterFinder.prototype.onModelChange = function () {
    var make = this.makeField.value;
    var model = this.modelField.value;
    var snapshotLights = (make && model) ? (this.snapshot.makes[make][model] || []) : [];

    this.applyLights(snapshotLights);
    this.setStatus("");

    if (!model || !this.shop) return;
    var self = this;
    var mine = ++this.token.light;
    ENGINE.fetchFacets(this.shop, { model: model }).then(function (live) {
      if (mine !== self.token.light || self.modelField.value !== model) return;
      if (!live.lights.length) return;
      self.applyLights(live.lights);
    }).catch(function () {});
  };

  /*
    85% of models fit exactly one undercarriage, so asking a question with a
    single answer is friction. Pick it and hide the field. The list already
    leads with "No Light/Laser" wherever it applies.
  */
  HolsterFinder.prototype.applyLights = function (lights) {
    this.fill(this.underField, lights, this.underPlaceholder);

    if (lights.length === 1) {
      this.underField.value = labelOf(lights[0]);
      this.showUndercarriage(false);
    } else {
      this.showUndercarriage(lights.length > 1);
    }
  };

  HolsterFinder.prototype.showUndercarriage = function (show) {
    if (this.underWrap) this.underWrap.hidden = !show;
  };

  HolsterFinder.prototype.hydrateFromUrl = function () {
    var universal = this.snapshot.universalModels || [];
    var selection = ENGINE.readUrl(window.location.search, universal);
    if (!selection) return;

    var makes = this.snapshot.makes;
    var make = Object.keys(makes).filter(function (name) {
      return Object.prototype.hasOwnProperty.call(makes[name], selection.model);
    })[0];
    if (!make) return;

    this.makeField.value = make;
    this.onMakeChange();
    this.modelField.value = selection.model;
    this.onModelChange();

    if (selection.undercarriage) {
      var offered = makes[make][selection.model] || [];
      if (offered.indexOf(selection.undercarriage) !== -1) {
        this.underField.value = selection.undercarriage;
      }
    }

    this.renderSummary({
      model: selection.model,
      undercarriage: this.underField.value || null
    });

    /*
      The reliable one. It fires on the RESULTS page, after navigation, so no
      beacon is racing an unload. Use it as the denominator for outcome
      analysis: a GA4 session segment on this event answers "did people who
      used the finder go on to add to cart", which holster_finder_submit
      cannot, because some of those beacons will be lost.

      It also fires for someone who arrives on a shared or bookmarked filtered
      URL without ever touching the dropdowns. That is correct for measuring
      outcomes and wrong for measuring widget engagement, so pair it with
      holster_finder_submit rather than reading either alone.
    */
    var resultsKey = [make, selection.model, this.underField.value || ""].join("|");
    if (resultsKey !== lastResultsKey) {
      lastResultsKey = resultsKey;
      track("holster_finder_results", {
        hf_make: make || null,
        hf_model: selection.model || null,
        hf_light: this.underField.value || null,
        hf_template: templateName()
      });
    }
  };

  /*
    Once a gun is chosen the three dropdowns have done their job, so on a
    results page the finder collapses to a single line naming the gun. That
    takes the section from about 280px to about 60px on a phone, which is a
    far larger saving than any amount of padding trimming, and it is the
    established pattern for vehicle and fitment selectors. Change puts the
    dropdowns back.
  */
  HolsterFinder.prototype.collapse = function () {
    this.section.setAttribute("data-rg-hf-collapsed", "true");
    this.dock.setAttribute("data-rg-hf-dock", "true");
    /*
      Marks the page as having an active gun. The only thing that reads it is
      the rule hiding the search engine's own search block on the results
      page: a shopper who arrived through the finder did not ask that
      question, and it costs 112px, 14% of a phone screen. Set on body rather
      than on the section because the block it hides is elsewhere in the page.
    */
    document.body.setAttribute("data-rg-hf-active", "true");
    // Only start measuring once there is a bar to position.
    if (!HolsterFinder.watching) {
      HolsterFinder.watching = true;
      watchDockOffset();
    }
  };

  HolsterFinder.prototype.expand = function () {
    this.section.removeAttribute("data-rg-hf-collapsed");
    this.dock.removeAttribute("data-rg-hf-dock");
    document.body.removeAttribute("data-rg-hf-active");
    /*
      Hide the summary while the form is open. With the dropdowns visible it
      just restates what they already say, one line lower, which is the
      redundant box Pablo saw when changing models. It earns its place only
      when the form is hidden.
    */
    if (this.summary) this.summary.hidden = true;
    // Focus the first field so the keyboard-only path works and the shopper
    // can see where the form reappeared.
    if (this.makeField) this.makeField.focus();
  };

  /*
    Re-read the URL after something else changed it. If our gun filter is gone,
    the bar stops claiming one and the form comes back.
  */
  HolsterFinder.prototype.syncToUrl = function () {
    var universal = this.snapshot.universalModels || [];
    var selection = ENGINE.readUrl(window.location.search, universal);
    var claiming = this.section.hasAttribute("data-rg-hf-collapsed");

    if (!selection && claiming) {
      if (this.summary) this.summary.hidden = true;
      this.expand();
      return;
    }
    if (selection) {
      this.hydrateFromUrl();
    }
  };

  /*
    State the constraint in words. Repopulated dropdowns tell the shopper what
    they chose; they do not tell them the page is filtered.
  */
  HolsterFinder.prototype.renderSummary = function (selection) {
    if (!this.summary || !this.summaryText) return;
    /*
      Reads as a sentence: "Glock G19X (Gen 1-6) with No Light/Laser".
      "No Light/Laser" is stated rather than omitted, per Pablo: it is part of
      what was asked for, and saying it makes clear the results assume a bare
      gun. On a phone the docked bar is a single line, so a long combination
      ellipsises rather than wrapping.
    */
    var text = selection.model;
    if (selection.undercarriage) {
      text += " with " + shortLight(selection.undercarriage);
    }
    this.summaryText.textContent = text;
    this.summary.hidden = false;
    this.collapse();
  };

  /*
    The submit button is deliberately never disabled: a disabled button says no
    without saying why, leaving the shopper to guess which field is at fault.
  */
  HolsterFinder.prototype.firstIncomplete = function () {
    if (!this.makeField.value) return { field: this.makeField, message: "Choose a gun make." };
    if (!this.modelField.value) return { field: this.modelField, message: "Choose a gun model." };
    if (this.underWrap && !this.underWrap.hidden && !this.underField.value) {
      return { field: this.underField, message: "Tell us about your light or laser." };
    }
    return null;
  };

  HolsterFinder.prototype.go = function () {
    var gap = this.firstIncomplete();
    if (gap) {
      /*
        Tried to submit without finishing. Worth its own event: a high rate
        here means the form is asking for something the shopper cannot supply,
        which is a fixable problem and is invisible in a submit-only funnel.
      */
      track("holster_finder_blocked", {
        hf_missing: gap.field === this.makeField ? "make" : (gap.field === this.modelField ? "model" : "light"),
        hf_make: this.makeField.value || null,
        hf_model: this.modelField.value || null
      });
      this.setStatus(gap.message);
      gap.field.focus();
      return;
    }
    this.setStatus("");

    /*
      Fired immediately before navigation, so it races the unload. GA4 sends
      through sendBeacon, which survives it, but the tag still has to have been
      evaluated. That race is why holster_finder_results exists on the landing
      side: treat THAT as the reliable denominator and this one as intent.
      Deliberately no artificial delay here; latency on the buying path costs
      more than a measurement.
    */
    track("holster_finder_submit", {
      hf_make: this.makeField.value || null,
      hf_model: this.modelField.value || null,
      hf_light: this.underField.value || null,
      hf_template: templateName()
    });

    window.location.href = ENGINE.buildUrl(
      {
        model: this.modelField.value,
        undercarriage: this.underField.value || null
      },
      {
        includeLightFit: this.includeLightFit,
        universalModels: this.snapshot.universalModels || [],
        universalUndercarriages: this.snapshot.universalUndercarriages || []
      }
    );
  };

  function init() {
    var roots = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < roots.length; i++) {
      if (!roots[i].hasAttribute("data-rg-hf-ready")) {
        roots[i].setAttribute("data-rg-hf-ready", "true");
        new HolsterFinder(roots[i]);
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
